'use client'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import Head from 'next/head'

const PrivacyPolicy = () => {
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (window.scrollY / totalHeight) * 100
      setScrollProgress(progress)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <Head>
        <title>Privacy Policy - Infinitcode</title>
        <meta name="description" content="Privacy Policy for Infinitcode - Learn how we collect, use, and protect your personal information." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <style jsx>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }

        .animated-border {
          position: relative;
          background: linear-gradient(#041536, #041536) padding-box,
                      conic-gradient(from var(--angle), transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%) border-box;
          border: 2px solid transparent;
          border-radius: 1rem;
          animation: spin 8s linear infinite;
        }

        .glow-effect {
          position: absolute;
          inset: -15px;
          background: conic-gradient(from var(--angle), transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
          border-radius: 1.5rem;
          filter: blur(15px);
          opacity: 0.3;
          animation: spin 8s linear infinite;
          z-index: -1;
          pointer-events: none;
        }

        @keyframes spin {
          from {
            --angle: 0deg;
          }
          to {
            --angle: 360deg;
          }
        }

        /* Fallback for browsers that don't support @property or custom properties in gradients */
        @supports not (background: conic-gradient(from var(--angle), red, blue)) {
          .animated-border {
            background: #041536;
            border: none;
            overflow: hidden;
          }

          .animated-border::before {
            content: '';
            position: absolute;
            inset: -2px;
            background: conic-gradient(from 0deg at 50% 50%, transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
            border-radius: inherit;
            z-index: -1;
            animation: rotateFallback 8s linear infinite;
          }

          .animated-border::after {
            content: '';
            position: absolute;
            inset: 2px;
            background: #041536;
            border-radius: calc(1rem - 2px);
            z-index: -1;
          }

          .glow-effect {
            background: conic-gradient(from 0deg at 50% 50%, transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
            animation: rotateFallback 8s linear infinite;
          }

          @keyframes rotateFallback {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        }

        .floating-shapes {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: -1;
        }

        .shape {
          position: absolute;
          opacity: 0.1;
          animation: float 20s infinite linear;
        }

        .shape:nth-child(1) {
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape:nth-child(2) {
          top: 60%;
          right: 10%;
          animation-delay: -10s;
        }

        .shape:nth-child(3) {
          bottom: 20%;
          left: 20%;
          animation-delay: -5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .section-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, #3BCF8E, transparent);
          margin: 2rem 0;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #041536;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #3BCF8E, #2DA771);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #2DA771, #3BCF8E);
        }
      `}</style>

      <div className="bg-gradient-to-br from-[#020F2A] via-[#041536] to-[#020F2A] text-white min-h-screen">
        {/* Scroll Progress Indicator */}
        <div 
          className="fixed top-0 left-0 h-1 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] z-50 transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
        
        {/* Floating Background Shapes */}
        <div className="floating-shapes">
          <div className="shape w-20 h-20 bg-gradient-to-r from-[#3BCF8E]/20 to-[#2DA771]/20 rounded-full"></div>
          <div className="shape w-16 h-16 bg-gradient-to-r from-[#2DA771]/20 to-[#3BCF8E]/20 rounded-lg rotate-45"></div>
          <div className="shape w-12 h-12 bg-[#3BCF8E]/20 rounded-full"></div>
        </div>

        {/* Header */}
        <header className="relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 py-16">
            {/* Back Button */}
            <div className="mb-8">
              <Link 
                href="/"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-[#3BCF8E] transition-all duration-300 group"
              >
                <svg 
                  className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>

            <div className="text-center">
              <div className="relative">
                <div className="glow-effect"></div>
                <div className="animated-border p-12 bg-gradient-to-br from-[#041536]/90 to-[#020F2A]/90">
                  <h1 className="text-5xl md:text-6xl font-bold mb-4">
                    Privacy{' '}
                    <span className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-transparent bg-clip-text">
                      Policy
                    </span>
                  </h1>
                  <div className="text-lg text-left flex items-center gap-10 justify-center text-gray-300">
                    <p>
                      <span className="text-[#3BCF8E] font-semibold">Last Updated:</span> 30-Jun-2025
                    </p>
                    <p>
                      <span className="text-[#3BCF8E] font-semibold">Effective Date:</span> 30-Jun-2025
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-6 pb-16">
          {/* Introduction */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/60 to-[#020F2A]/60 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/20">
              <p className="text-lg leading-relaxed text-gray-200">
                This Privacy Policy describes the policies of{' '}
                <strong className="text-[#3BCF8E]">Infinitcode SHPK</strong>, Tirane Tirane Njesia Nr 2,
                Rr.Qemal Stafa, Pallati 118, Shkalla1, AP 18, Tirane, Albania, Tirana 1000, Albania, email:{' '}
                <Link 
                  href="mailto:hello@infinitcode.com" 
                  className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                >
                  hello@infinitcode.com
                </Link>
                , phone:{' '}
                <Link 
                  href="tel:+38971666022" 
                  className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                >
                  +38971666022
                </Link>{' '}
                on the collection, use and disclosure of your information that we collect when you use our website (
                <Link 
                  href="https://infinitcode.ai/" 
                  className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                >
                  https://infinitcode.ai/
                </Link>
                ). (the &quot;Service&quot;). By accessing or using the Service, you are consenting to the collection, use and
                disclosure of your information in accordance with this Privacy Policy. If you do not consent to the same,
                please do not access or use the Service.
              </p>
            </div>
          </section>

          {/* Policy Modification Notice */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-6 border border-[#3BCF8E]/30">
              <p className="text-gray-200 leading-relaxed">
                We may modify this Privacy Policy at any time without any prior notice to you and will post the revised
                Privacy Policy on the Service. The revised Policy will be effective 180 days from when the revised Policy
                is posted in the Service and your continued access or use of the Service after such time will constitute
                your acceptance of the revised Privacy Policy. We therefore recommend that you periodically review this page.
              </p>
            </div>
          </section>

          {/* Policy Sections */}
          <div className="space-y-8">
            {/* 1. Information We Collect */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">1. Information We Collect</h2>
              <p className="text-gray-200 mb-4">We will collect and process the following personal information about you:</p>
              <ul className="list-disc list-inside text-gray-200 ml-4">
                <li>Email</li>
              </ul>
            </section>

            <div className="section-divider"></div>

            {/* 2. How We Collect Your Information */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">2. How We Collect Your Information</h2>
              <p className="text-gray-200 mb-4">We collect/receive information about you in the following manner:</p>
              <ol className="list-decimal list-inside text-gray-200 ml-4 space-y-2">
                <li>When a user fills up the registration form or otherwise submits personal information</li>
                <li>Interacts with the website</li>
                <li>From public sources</li>
              </ol>
            </section>

            <div className="section-divider"></div>

            {/* 3. How We Use Your Information */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">3. How We Use Your Information</h2>
              <p className="text-gray-200 mb-4">We will use the information that we collect about you for the following purposes:</p>
              <ol className="list-decimal list-inside text-gray-200 ml-4 space-y-2 mb-6">
                <li>Marketing/Promotional</li>
                <li>Creating user account</li>
                <li>Customer feedback collection</li>
                <li>Enforce T&C</li>
                <li>Processing payment</li>
                <li>Support</li>
                <li>Administration info</li>
                <li>Manage user account</li>
              </ol>
              <p className="text-gray-200">
                If we want to use your information for any other purpose, we will ask you for consent and will use your
                information only on receiving your consent and then, only for the purpose(s) for which grant consent unless
                we are required to do otherwise by law.
              </p>
            </section>

            <div className="section-divider"></div>

            {/* 4. How We Share Your Information */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">4. How We Share Your Information</h2>
              <p className="text-gray-200 mb-4">
                We will not transfer your personal information to any third party without seeking your consent,
                except in limited circumstances as described below:
              </p>
              <ol className="list-decimal list-inside text-gray-200 ml-4 space-y-2 mb-6">
                <li>Ad service</li>
                <li>Analytics</li>
              </ol>
              <div className="space-y-4 text-gray-200">
                <p>
                  We require such third party&apos;s to use the personal information we transfer to them only for the purpose
                  for which it was transferred and not to retain it for longer than is required for fulfilling the said purpose.
                </p>
                <p>
                  We may also disclose your personal information for the following: (1) to comply with applicable law,
                  regulation, court order or other legal process; (2) to enforce your agreements with us, including this
                  Privacy Policy; or (3) to respond to claims that your use of the Service violates any third-party rights.
                  If the Service or our company is merged or acquired with another company, your information will be one of
                  the assets that is transferred to the new owner.
                </p>
              </div>
            </section>

            <div className="section-divider"></div>

            {/* 5. Retention Of Your Information */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">5. Retention Of Your Information</h2>
              <p className="text-gray-200">
                We will retain your personal information with us for{' '}
                <strong className="text-[#3BCF8E]">90 days to 2 years after users terminate their accounts</strong> or for as
                long as we need it to fulfill the purposes for which it was collected as detailed in this Privacy Policy.
                We may need to retain certain information for longer periods such as record-keeping/reporting in accordance with
                applicable law or for other legitimate reasons like enforcement of legal rights, fraud prevention, etc. Residual
                anonymous information and aggregate information, neither of which identifies you (directly or indirectly), may be
                stored indefinitely.
              </p>
            </section>

            <div className="section-divider"></div>

            {/* 6. Your Rights */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">6. Your Rights</h2>
              <div className="space-y-4 text-gray-200">
                <p>
                  Depending on the law that applies, you may have a right to access and rectify or erase your personal data
                  or receive a copy of your personal data, restrict or object to the active processing of your data, ask us to
                  share (port) your personal information to another entity, withdraw any consent you provided to us to process
                  your data, a right to lodge a complaint with a statutory authority and such other rights as may be relevant
                  under applicable laws. To exercise these rights, you can write to us at{' '}
                  <Link 
                    href="mailto:hello@infinitcode.com" 
                    className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                  >
                    hello@infinitcode.com
                  </Link>
                  . We will respond to your request in accordance with applicable law.
                </p>
                <p>
                  You may opt-out of direct marketing communications or the profiling we carry out for marketing purposes by
                  writing to us at{' '}
                  <Link 
                    href="mailto:hello@infinitcode.com" 
                    className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                  >
                    hello@infinitcode.com
                  </Link>
                  .
                </p>
                <p>
                  Do note that if you do not allow us to collect or process the required personal information or withdraw the
                  consent to process the same for the required purposes, you may not be able to access or use the services for
                  which your information was sought.
                </p>
              </div>
            </section>

            <div className="section-divider"></div>

            {/* 7. Cookies */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">7. Cookies Etc.</h2>
              <p className="text-gray-200">
                To learn more about how we use these and your choices in relation to these tracking technologies, please refer to our{' '}
                <Link 
                  href="https://infinitcode.ai/cookies-policy" 
                  className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors underline"
                >
                  Cookie Policy
                </Link>
                .
              </p>
            </section>

            <div className="section-divider"></div>

            {/* 8. Security */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">8. Security</h2>
              <p className="text-gray-200">
                The security of your information is important to us and we will use reasonable security measures to prevent the
                loss, misuse or unauthorized alteration of your information under our control. However, given the inherent risks,
                we cannot guarantee absolute security and consequently, we cannot ensure or warrant the security of any information
                you transmit to us and you do so at your own risk.
              </p>
            </section>

            <div className="section-divider"></div>

            {/* 9. Grievance / Data Protection Officer */}
            <section className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">9. Grievance / Data Protection Officer</h2>
              <p className="text-gray-200">
                If you have any queries or concerns about the processing of your information that is available with us, you may
                email our Grievance Officer at <strong className="text-[#3BCF8E]">Infinitcode SHPK</strong>, Tirane Tirane Njesia Nr 2,
                Rr.Qemal Stafa, Pallati 118, Shkalla1, AP 18, Tirane, Albania, email:{' '}
                <Link 
                  href="mailto:hello@infinitcode.com" 
                  className="text-[#3BCF8E] hover:text-[#2DA771] transition-colors"
                >
                  hello@infinitcode.com
                </Link>
                . We will address your concerns in accordance with applicable law.
              </p>
            </section>
          </div>
        </main>
      </div>
    </>
  )
}

export default PrivacyPolicy